import { Stack } from '@mui/material'
import i18n from 'i18next'
import _ from 'lodash'
import { DateTime } from 'luxon'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiDownload, BiImages, BiPlus } from 'react-icons/bi'
import { ReactImageGalleryItem } from 'react-image-gallery'

import {
  AgencySettings_EnabledFeaturesEnum,
  Favorite_PageIdEnum,
  SmartPatrolOffense,
  SmartPatrolOffenseAggregation,
} from 'src/api/gen'
import { ISmartPatrolOffenseListFilters } from 'src/api/types/smartPatrol'
import { getVehicleCategoryIcon } from 'src/commons/constants'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { getJurisdictionTimezone } from 'src/commons/temporalHelpers'
import InfoTooltip from 'src/components/Tooltip/InfoTooltip'
import { useCheckEnabledFeature } from 'src/layout/AgencySettings'
import BaseIconButton from 'src/muiv5/components/commons/BaseIconButton'
import CardContent from 'src/muiv5/components/commons/Cards/CardContent'
import CardHeader from 'src/muiv5/components/commons/Cards/CardHeader/CardHeader'
import CardHeaderActions from 'src/muiv5/components/commons/Cards/CardHeader/CardHeaderActions'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'
import { DateCell } from 'src/muiv5/components/commons/DataTable/CellRenderer/DateCell'
import { IconCell } from 'src/muiv5/components/commons/DataTable/CellRenderer/IconCell'
import { ProviderCell } from 'src/muiv5/components/commons/DataTable/CellRenderer/ProviderCell'
import { TagCell } from 'src/muiv5/components/commons/DataTable/CellRenderer/TagCell'
import DataTable, { IColumnItemsProps } from 'src/muiv5/components/commons/DataTable/DataTable'
import SearchInput from 'src/muiv5/components/commons/Inputs/SearchInput'
import SmartPatrolOffenseDialog from 'src/muiv5/components/SmartPatrol/SmartPatrolOffenseDialog'

import { downloadSmartPatrolOffensesImages, exportSmartPatrolOffenses, fetchSmartPatrolOffenses } from './requests'
import SmartPatrolImagesDialog from './SmartPatrolImagesDialog'
import SmartPatrolSidePanel from './SmartPatrolSidePanel'

export interface ISmartPatrolDataType extends Omit<SmartPatrolOffense, 'reportingDateTime'> {
  reportingDateTime: string
}

export function getInitialFilters(): ISmartPatrolOffenseListFilters {
  return {
    id: undefined,
    vin: undefined,
    vehicule_types: [],
    provider_names: [],
    created_at_after: undefined,
    created_at_before: undefined,
    agent_names: [],
  }
}

export function SmartPatrol() {
  const { t } = useTranslation()
  const defaultFilters = useMemo(getInitialFilters, [])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.policies_smart_patrol, defaultFilters)
  const displayedFiltersRef = useRef<ISmartPatrolOffenseListFilters>()
  const [offensesData, setOffensesData] = useState<SmartPatrolOffense[]>([])
  const [displayedRowData, setdisplayedRowData] = useState<ISmartPatrolDataType | null>(null)
  const [isCreateOffenseDialogOpen, setIsCreateOffenseDialogOpen] = useState<boolean>(false)
  const canCreateSmartPatrolOffense = useCheckEnabledFeature()(
    AgencySettings_EnabledFeaturesEnum['smart_patrol.create_from_web']
  )
  const [isOverDimension, setIsOverDimension] = useState<boolean>(false)
  const [offensesAggregations, setOffensesAggregations] = useState<SmartPatrolOffenseAggregation>({
    provider_names: {},
    vehicule_types: {},
    agent_names: {},
    type: {},
    total: 0,
  })

  const columns = [
    {
      label: t('smart_patrol.violation_id'),
      width: 300,
      dataKey: 'id',
    },
    {
      label: 'VIN',
      width: 150,
      dataKey: 'vin',
    },
    {
      label: t('smart_patrol.provider'),
      width: 200,
      dataKey: 'provider_name',
      cellRenderer: (props) => <ProviderCell {...props} field="name" />,
    },
    {
      label: t('smart_patrol.vehicle_type'),
      width: 250,
      dataKey: 'vehicleType',
      cellRenderer: (props) => <IconCell {...props} icon={getVehicleCategoryIcon(props.cellData)} />,
    },
    {
      label: t('smart_patrol.reporting_datetime'),
      width: 380,
      dataKey: 'reportingDateTime',
      cellRenderer: (props) => <DateCell isoDate outputFormat={DateTime.DATETIME_MED} {...props} />,
    },
    {
      label: t('smart_patrol.violation_type'),
      width: 250,
      dataKey: 'type',
      cellRenderer: (props) => <TagCell {...props} />,
    },
    {
      label: t('smart_patrol.agent_name'),
      width: 380,
      dataKey: 'agentName',
    },
    {
      label: t('smart_patrol.images'),
      width: 250,
      dataKey: 'images',
      cellRenderer: (props) => {
        return (
          <Stack direction="row" gap={1} key={`${props.rowIndex}-stack`}>
            <BaseIconButton
              key={`${props.rowIndex}-images`}
              onClick={() => handleImagesClick(props.rowData)}
              disabled={!props?.rowData['images']?.length}
            >
              <BiImages />
            </BaseIconButton>
            <BaseIconButton
              disabled={!props.rowData['image_download_url']}
              key={`${props.rowIndex}-download`}
              onClick={() => handleImagesDownloadClick(props.rowData['id'])}
            >
              <BiDownload />
            </BaseIconButton>
          </Stack>
        )
      },
    },
  ] as IColumnItemsProps[]

  const handleImagesClick = (rowData: ISmartPatrolDataType) => {
    setdisplayedRowData(rowData)
  }

  const handleImagesDownloadClick = async (offense_uid: string) => {
    downloadSmartPatrolOffensesImages({ offense_uid })
  }

  const handleCloseDialog = () => {
    setdisplayedRowData(null)
  }

  const handleSearchChange = (value: string) => {
    setFilters((pendingFilters) => ({ ...pendingFilters, vin: value.split(',') }))
  }

  /**
   * Return React Image Gallery compatible object from image URL
   * @param imageUrl the URL of the image to convert
   * @returns
   */
  const getGaleryImage = (imageUrl: string): ReactImageGalleryItem => ({
    original: imageUrl,
    thumbnail: imageUrl,
  })

  /**
   *
   */
  const handleExportClick = async () => {
    try {
      await exportSmartPatrolOffenses({
        // Export dates in the timezone and language of the user
        id: filters.id,
        vin: filters.vin,
        vehicule_types: filters.vehicule_types,
        provider_names: filters.provider_names,
        type: filters.type,
        created_at_after: filters.created_at_after,
        created_at_before: filters.created_at_before,
        agent_names: filters.agent_names,
        tz: getJurisdictionTimezone(),
        lang: i18n.language,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const fetchData = (signal: AbortSignal) => {
    fetchSmartPatrolOffenses({
      id: filters.id,
      vin: filters.vin,
      vehicule_types: filters.vehicule_types,
      provider_names: filters.provider_names,
      type: filters.type,
      created_at_after: filters.created_at_after,
      created_at_before: filters.created_at_before,
      agent_names: filters.agent_names,
      options: { signal },
    })
      .then((data) => {
        setOffensesData(data.results)
        setOffensesAggregations(data.aggregations)
        setIsOverDimension(offensesData.length < offensesAggregations.total)
      })
      .catch((reason) => {
        if (!signal.aborted) {
          console.error(reason)
        }
      })
  }

  useDeepCompareEffect(() => {
    const abortController = new AbortController()
    fetchData(abortController.signal)
    return () => abortController.abort()
  }, [filters])

  function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  return (
    <FilteringCard
      onApplyFilter={onApplyFilterClick}
      SideComponent={
        <SmartPatrolSidePanel
          aggregations={offensesAggregations}
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
        />
      }
    >
      <CardHeader title="Citation List">
        <CardHeaderActions>
          <SearchInput onChange={(evt) => handleSearchChange(evt.target.value)} />
          {isOverDimension && <InfoTooltip rowCount={offensesData.length} />}
          {canCreateSmartPatrolOffense && (
            <BaseIconButton
              onClick={() => setIsCreateOffenseDialogOpen(true)}
              title={t('smart_patrol.create_offense.title')}
            >
              <BiPlus />
            </BaseIconButton>
          )}
          <BaseIconButton onClick={handleExportClick}>
            <BiDownload />
          </BaseIconButton>
        </CardHeaderActions>
      </CardHeader>
      <CardContent>
        <DataTable items={offensesData} columns={columns} />
      </CardContent>
      {!!displayedRowData && (
        <SmartPatrolImagesDialog
          agentName={displayedRowData.agentName}
          type={displayedRowData.type}
          vehicleType={displayedRowData.vehicleType}
          date={displayedRowData.reportingDateTime}
          downloadUrl={displayedRowData.image_download_url}
          images={(displayedRowData.images ?? []).map((i) => getGaleryImage(i))}
          comment={displayedRowData.comment}
          onCloseClick={handleCloseDialog}
          onDownloadClick={() => handleImagesDownloadClick(displayedRowData?.id ?? '')}
        />
      )}
      {canCreateSmartPatrolOffense && (
        <SmartPatrolOffenseDialog
          open={isCreateOffenseDialogOpen}
          onClose={() => setIsCreateOffenseDialogOpen(false)}
          onCreated={() => fetchData(new AbortController().signal)}
        />
      )}
    </FilteringCard>
  )
}
